import type { ExperimentReport } from './api'
import { STATE_PAIRS, type JournalState } from '../selfReport'

const KEY = 'brainspot:experiment-report-drafts'
export const IN_BETWEEN = [
  { value: 'coffee', label: 'Coffee' },
  { value: 'headband_off', label: 'Took the headband off' },
  { value: 'call', label: 'Phone call' },
  { value: 'food', label: 'Food' },
  { value: 'nothing', label: 'Nothing' },
  { value: 'other', label: 'Other' },
] as const

export interface ReportDraft {
  state: JournalState | null
  stress: number | null
  planned: ExperimentReport['activity_as_planned'] | null
  between: ExperimentReport['in_between']
}

export const EMPTY_REPORT: ReportDraft = { state: null, stress: null, planned: null, between: [] }
type Kept = { account: string; runs: Record<string, ReportDraft> }

function read(): Kept | null {
  try {
    const raw: unknown = JSON.parse(sessionStorage.getItem(KEY) ?? 'null')
    if (!raw || typeof raw !== 'object' || !('account' in raw) || !('runs' in raw)) return null
    if (typeof raw.account !== 'string' || !raw.runs || typeof raw.runs !== 'object') return null
    return raw as Kept
  } catch { return null }
}

export function clearReportDrafts(): void {
  try { sessionStorage.removeItem(KEY) } catch { /* Storage is optional. */ }
}

/** An account switch clears the previous person's words without reading them. */
export function adoptReportAccount(account: string): void {
  const kept = read()
  if (kept && kept.account !== account) clearReportDrafts()
}

export function peekReportDraft(account: string, run: string): ReportDraft {
  const kept = read()
  const draft = kept?.account === account ? kept.runs[run] : null
  if (!draft || typeof draft !== 'object') return { ...EMPTY_REPORT, between: [] }
  return {
    state: draft.state !== null && Object.hasOwn(STATE_PAIRS, draft.state) ? draft.state : null,
    stress: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].includes(draft.stress as number) ? draft.stress : null,
    planned: ['as_planned', 'shorter', 'longer', 'other'].includes(draft.planned ?? '') ? draft.planned : null,
    between: Array.isArray(draft.between) ? IN_BETWEEN.filter((item) => draft.between.includes(item.value)).map((item) => item.value) : [],
  }
}

export function keepReportDraft(account: string, run: string, draft: ReportDraft): void {
  const previous = read()
  const runs = previous?.account === account ? previous.runs : {}
  try { sessionStorage.setItem(KEY, JSON.stringify({ account, runs: { ...runs, [run]: draft } })) } catch { /* The form still works without storage. */ }
}

export function forgetReportDraft(account: string, run: string): void {
  const kept = read()
  if (kept?.account !== account) return
  delete kept.runs[run]
  try {
    if (Object.keys(kept.runs).length === 0) clearReportDrafts()
    else sessionStorage.setItem(KEY, JSON.stringify(kept))
  } catch { /* Storage is optional. */ }
}

/** Unanswered is null, including felt state; there is no neutral default. */
export function reportAnswers(draft: ReportDraft, revision: number): ExperimentReport | null {
  if (draft.planned === null) return null
  return { schema_version: 2, revision, state: draft.state,
    state_quadrant: draft.state === null ? null : STATE_PAIRS[draft.state],
    stress_1_10: draft.stress, activity_as_planned: draft.planned, in_between: [...draft.between] }
}
